import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
// mui components
import { Avatar, CircularProgress, Divider, Grid } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
// icons
import CrossIcon from 'assets/icons/buttons/cross.svg';
// helpers
import { getImageBySize } from 'utils/helpers';
// atoms
import { postCommentsAtom } from 'atoms';
import { useAtomValue } from 'jotai';
import { useProfilesQuery } from 'redux-store/api/profiles';
// styles
import { likersListStyles } from '../../../likers/styles/index';

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <CrossIcon
          aria-label="close"
          onClick={onClose}
          style={{
            position: 'absolute',
            right: 21,
            top: 21,
            cursor: 'pointer',
          }}
        />
      ) : null}
    </DialogTitle>
  );
}

const CommentLikersListModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: profiles, isLoading } = useProfilesQuery();

  const likesCommentId = searchParams.get('likesCommentId');
  const likesCommentParentPath = searchParams.get('likesCommentParentPath');

  const comments = useAtomValue(postCommentsAtom(likesCommentParentPath || ''));
  const comment = comments.find((c) => c.id === likesCommentId) || null;

  const handleClose = () => {
    searchParams.delete('likesCommentId');
    searchParams.delete('likesCommentParentPath');
    setSearchParams(searchParams);
  };

  return (
    <div>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={searchParams.has('likesCommentId')}
        PaperProps={{
          sx: {
            width: '430px',
            borderRadius: '12px',
          },
        }}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          <Typography sx={likersListStyles.likesText}>Likes</Typography>
          <Typography sx={likersListStyles.membersText}>
            Here is a list of members who liked this comment
          </Typography>
        </BootstrapDialogTitle>
        <Divider sx={likersListStyles.divider} />
        <Grid sx={likersListStyles.scrollContainer}>
          <Grid sx={{ ...likersListStyles.likersContainer, height: 418, overflowY: 'auto' }}>
            {isLoading ? (
              <CircularProgress sx={likersListStyles.circularProgress} />
            ) : comment?.likes ? (
              comment.likes.map((userId) => {
                const user = profiles?.items.find((u) => u.id?.toString() === userId.toString());
                if (!user) return null;
                return (
                  <Grid sx={likersListStyles.likerContainer} key={user.id}>
                    <Avatar
                      alt="avatar"
                      sx={likersListStyles.userAvatar}
                      src={getImageBySize(user.avatar, 36, 36)}
                      variant="square"
                    />
                    <Typography sx={likersListStyles.userName}>
                      {`${user.firstName} ${user.lastName}`}
                    </Typography>
                  </Grid>
                );
              })
            ) : null}
          </Grid>
        </Grid>
      </BootstrapDialog>
    </div>
  );
};

export default CommentLikersListModal;
